import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import { Badge } from '@/common/ui/badge'
import { Button } from '@/common/ui/button'
import { Switch } from '@/common/ui/switch'
import { Edit, Trash2, Plus } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import type { BrokeFlexMessageCampaignModel } from '../../types'

interface CreateColumnsProps {
	onToggleActive: (id: string, currentStatus: boolean) => void
	onDelete: (campaign: BrokeFlexMessageCampaignModel) => void
	onEdit: (id: string) => void
	onAddStage: (campaign: BrokeFlexMessageCampaignModel) => void
	onEditStage: (
		campaign: BrokeFlexMessageCampaignModel,
		stage: any,
		stageNumber: number,
	) => void
}

export const createBrokeFlexMessageColumns = ({
	onToggleActive,
	onDelete,
	onEdit,
	onAddStage,
	onEditStage,
}: CreateColumnsProps): ColumnDef<BrokeFlexMessageCampaignModel>[] => [
	{
		accessorKey: 'name',
		header: 'ชื่อแคมเปญ',
		cell: ({ row }) => (
			<div className="max-w-xs truncate font-medium">
				{row.original.name || '-'}
			</div>
		),
	},
	{
		id: 'stages',
		header: 'ระยะ',
		cell: ({ row }) => {
			const stages = row.original.brokeNotificationSettings?.stages || []
			const sortedStages = [...stages].sort((a, b) => a.order - b.order)
			const stagesCount = stages.length
			const canAddMore = stagesCount < 4

			return (
				<div className="space-y-2">
					<div className="flex items-center gap-2">
						<Badge variant="secondary">
							{stagesCount} {stagesCount === 1 ? 'ระยะ' : 'ระยะ'}
						</Badge>
					</div>
					<div className="flex flex-wrap gap-2">
						{/* Show edit buttons for stages 2, 3, 4 */}
						{sortedStages.slice(1).map((stage, index) => (
							<Button
								key={stage.id}
								variant="ghost"
								size="sm"
								onClick={() => onEditStage(row.original, stage, index + 2)}
								className="h-7"
							>
								<Edit className="mr-1 h-3 w-3" />
								ระยะ {index + 2}
							</Button>
						))}
						{canAddMore && (
							<Button
								variant="outline"
								size="sm"
								onClick={() => onAddStage(row.original)}
								className="h-7"
							>
								<Plus className="mr-1 h-3 w-3" />
								เพิ่มระยะ
							</Button>
						)}
					</div>
				</div>
			)
		},
	},
	{
		accessorKey: 'isActive',
		header: 'สถานะ',
		cell: ({ row }) => (
			<div className="flex items-center gap-2">
				<Switch
					checked={row.original.isActive}
					onCheckedChange={() =>
						onToggleActive(row.original.id, row.original.isActive)
					}
				/>
				<span className="text-sm">
					{row.original.isActive ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
				</span>
			</div>
		),
	},
	{
		accessorKey: 'createdAt',
		header: 'วันที่สร้าง',
		cell: ({ row }) =>
			format(new Date(row.original.createdAt), 'dd MMM yyyy HH:mm', {
				locale: th,
			}),
	},
	{
		id: 'actions',
		header: () => <div className="text-right">จัดการ</div>,
		cell: ({ row }) => (
			<div className="flex justify-end gap-2">
				<Button
					variant="outline"
					size="sm"
					onClick={() => onEdit(row.original.id)}
				>
					<Edit className="mr-1 h-4 w-4" />
					แก้ไข
				</Button>
				<Button
					variant="outline"
					size="sm"
					onClick={() => onDelete(row.original)}
					className="text-red-600 hover:text-red-700"
				>
					<Trash2 className="mr-1 h-4 w-4" />
					ลบ
				</Button>
			</div>
		),
	},
]
